import { useContext, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import { StoresContext } from '@App/contexts'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import { PaginationBody } from '@Entities/pagination'
import { ResultPage } from '@Entities/result-page'
import { SuggestedMatch } from '@Entities/matching/suggested-match'

export const MATCH_HISTORY_QUERY_KEY = 'match-history'

/**
 * Owns the TanStack Query wiring for the Match History list. The sidebar's draft filter lives in the
 * store but only the *applied* filter is part of the key, so ticking a checkbox doesn't search.
 * The Songwriter Hub and the rightsholder portal share this store, so the account is part of the key too.
 */
export const useMatchHistorySearch = (isSongwriterHub: boolean) => {
    const { matchHistoryStore, rightsholderStore, songwriterStore } = useContext(StoresContext)
    const queryClient = useQueryClient()

    // Only read the id this view actually has — the other one throws when it isn't set.
    const getScopeId = () =>
        isSongwriterHub ? songwriterStore.currentSongwriterId : rightsholderStore.currentRightsholderId
    const buildQueryKey = () => [
        MATCH_HISTORY_QUERY_KEY,
        isSongwriterHub ? 'songwriter' : 'rightsholder',
        getScopeId(),
        matchHistoryStore.appliedStatusFilters,
        matchHistoryStore.matchSuggestionHistoryPaginationBody,
    ]

    const queryKey = buildQueryKey()
    const paginationBody = matchHistoryStore.matchSuggestionHistoryPaginationBody
    const statusFilters = matchHistoryStore.appliedStatusFilters

    // A URL-linked filter is applied by the filter section's mount effect, so wait for it rather than
    // firing an unfiltered request first.
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])

    const historyQuery = useSearchQuery<ResultPage<SuggestedMatch>>({
        queryKey,
        queryFn: async () =>
            matchHistoryStore.fetchMatchSuggestionHistory(
                statusFilters,
                paginationBody,
                isSongwriterHub ? songwriterStore.currentSongwriterId : undefined
            ),
        // The layout resets this store when the page is left, while the query cache keeps the page, so the
        // results have to be copied into the store whenever the query has them, not just after a fetch.
        applyData: (response) => matchHistoryStore.applyMatchSuggestionHistory(response, paginationBody, queryKey),
        // The store outlives this page (e.g. coming back from a submission), but only reuse its results
        // when they were fetched under this exact key.
        initialData: isEqual(matchHistoryStore.lastResultsKey, queryKey)
            ? matchHistoryStore.matchSuggestionHistory
            : undefined,
        enabled: mounted,
        onError: (error) => matchHistoryStore.reportSearchError(error),
    })

    const applyFilters = () => {
        matchHistoryStore.applyMatchSuggestionHistoryFilter()
        // Reapplying the same filter on the same page leaves the key unchanged, so a key-driven refetch
        // alone wouldn't fire — the old button always searched again.
        if (isEqual(queryKey, buildQueryKey())) {
            queryClient.invalidateQueries({ queryKey: [MATCH_HISTORY_QUERY_KEY] })
        }
    }

    const onChangePage = (body: PaginationBody) => matchHistoryStore.setMatchSuggestionHistoryPaginationBody(body)

    const onChangeLimit = (newLimit: number) => matchHistoryStore.setMatchSuggestionHistoryResultsLimit(newLimit)

    return { historyQuery, applyFilters, onChangePage, onChangeLimit }
}

export default useMatchHistorySearch
